/**
 * Converts an RGB color string to a hex color code
 * @param rgb - RGB color string in format 'rgb(r g b)'
 * @returns Hex color code in format '#rrggbb'
 */
export function rgbToHex(rgb: string): string {
    // Remove 'rgb(' and ')' and split the values by spaces
    const [r, g, b] = rgb.replace('rgb(', '').replace(')', '').split(/\s+/).map(Number);
    return (
        '#' +
        [r, g, b]
            .map((x) => {
                const hex = x.toString(16);
                return hex.length === 1 ? '0' + hex : hex;
            })
            .join('')
    );
}

/**
 * Generates a consistent hex color from a given text string
 * @param text - The text to generate a color from
 * @returns Hex color code in format '#rrggbb'
 */
export function getColourFromText(text: string): string {
    const normalised = text.toLowerCase();

    // Generate a hash from the text
    let hash = 0;
    for (let i = 0; i < normalised.length; i++) {
        hash = normalised.charCodeAt(i) + ((hash << 5) - hash);
        hash = hash & hash;
    }

    // Convert hash to hue (0-360)
    const hue = Math.abs(hash) % 360;

    // Use fixed saturation and lightness for pleasant, readable colors
    const saturation = 65;
    const lightness = 45;

    // Convert HSL to RGB then to hex
    const h = hue / 360;
    const s = saturation / 100;
    const l = lightness / 100;

    const hueToRgb = (p: number, q: number, t: number): number => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    const r = Math.round(hueToRgb(p, q, h + 1 / 3) * 255);
    const g = Math.round(hueToRgb(p, q, h) * 255);
    const b = Math.round(hueToRgb(p, q, h - 1 / 3) * 255);

    return (
        '#' +
        [r, g, b]
            .map((x) => {
                const hex = x.toString(16);
                return hex.length === 1 ? '0' + hex : hex;
            })
            .join('')
    );
}
